import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";

// Generic delayed announcement popup, shown once per browser session (not
// permanently dismissed via localStorage) — a set delay after landing on a
// page. Uses sessionStorage so returning visitors on a new visit see it
// again, while it doesn't repeat on every page navigation within one visit.
export const DelayedAnnouncementModal = ({
  storageKey,
  delayMs = 45000,
  testId,
  image,
  imageAlt,
  imageObjectPosition = "50% 35%",
  overline,
  heading,
  body,
  ctaLabel,
  ctaHref,
}) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem(storageKey)) return;
    const timer = setTimeout(() => setOpen(true), delayMs);
    return () => clearTimeout(timer);
  }, [storageKey, delayMs]);

  const dismiss = () => {
    sessionStorage.setItem(storageKey, "1");
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) dismiss(); }}>
      <DialogContent
        data-testid={testId}
        className="max-w-md bg-[var(--bg-secondary)] border border-[var(--border-default)] p-0 gap-0 overflow-hidden [&>button:last-child]:hidden"
      >
        <button
          onClick={dismiss}
          data-testid={`${testId}-close`}
          aria-label="Close"
          className="absolute right-4 top-4 text-white hover:text-[var(--brand-primary)] transition-colors z-10 bg-black/40 rounded-full p-1.5"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="relative aspect-[16/9] overflow-hidden">
          <img
            src={image}
            alt={imageAlt}
            className="w-full h-full object-cover"
            style={{ objectPosition: imageObjectPosition }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-secondary)] via-transparent to-transparent" />
        </div>
        <div className="p-7 md:p-8 pt-5">
          <p className="overline mb-3">{overline}</p>
          <h2 className="font-display text-2xl md:text-3xl text-white leading-tight mb-3">
            {heading}
          </h2>
          <p className="text-base text-[var(--text-secondary)] leading-relaxed">
            {body}
          </p>
          {ctaHref ? (
            <a
              href={ctaHref}
              onClick={dismiss}
              data-testid={`${testId}-dismiss`}
              className="mt-6 block w-full text-center bg-[var(--brand-primary)] text-black font-mono text-xs uppercase tracking-[0.15em] py-3 hover:bg-[var(--brand-primary)]/90 transition-colors"
            >
              {ctaLabel}
            </a>
          ) : (
            <button
              onClick={dismiss}
              data-testid={`${testId}-dismiss`}
              className="mt-6 w-full bg-[var(--brand-primary)] text-black font-mono text-xs uppercase tracking-[0.15em] py-3 hover:bg-[var(--brand-primary)]/90 transition-colors"
            >
              {ctaLabel}
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
